import { AnimatePresence, motion } from 'framer-motion'
import {
  Check,
  ChevronDown,
  Compass,
  CreditCard,
  Truck,
} from 'lucide-react'
import { useState } from 'react'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Badge } from 'src/shared/components/ui/badge'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { QUOTE_STATUS_PHASES } from 'src/shared/consts/quoteStatus'
import { cn } from 'src/shared/components/ui/utils'

export const PHASE_UI = [
  {
    icon: Compass,
    gradient: 'from-slate-400 to-slate-600',
    ring: 'ring-slate-200',
    badge: 'gray' as const,
  },
  {
    icon: CreditCard,
    gradient: 'from-orange-400 to-orange-600',
    ring: 'ring-orange-200',
    badge: 'orange' as const,
  },
  {
    icon: Truck,
    gradient: 'from-blue-400 to-blue-600',
    ring: 'ring-blue-200',
    badge: 'blue' as const,
  },
] as const

export const SUB_STATUS_BADGE: Record<
  string,
  'gray' | 'yellow' | 'orange' | 'blue' | 'purple' | 'green'
> = {
  secondary: 'gray',
  yellow: 'yellow',
  orange: 'orange',
  blue: 'blue',
  purple: 'purple',
  green: 'green',
}

export const OrderProgressPopover = ({
  currentPhaseIndex,
  currentLabel,
}: {
  currentPhaseIndex: number
  currentLabel: string
}) => {
  const { t } = useLocalization()
  const [expandedPhases, setExpandedPhases] = useState<Set<string>>(
    () => new Set()
  )

  const togglePhase = (key: string) => {
    setExpandedPhases((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  return (
    <Column className="gap-0">
      <Column className="gap-3 border-b border-gray-100 bg-gradient-to-br from-gray-50/90 via-white to-white px-4 py-4">
        <Column className="gap-1">
          <h4>{t('Order progress', 'Orderförlopp')}</h4>
          <p>
            {t(
              'Track your request from quote to delivery || Följ din förfrågan från offert till leverans'
            )}
          </p>
        </Column>
      </Column>

      <Column className="max-h-[min(360px,50vh)] overflow-y-auto overscroll-contain px-4 py-3">
        <Column className="gap-0">
          {QUOTE_STATUS_PHASES.map((phase, index) => {
            const ui = PHASE_UI[index] ?? PHASE_UI[0]
            const Icon = ui.icon
            const isComplete = index < currentPhaseIndex
            const isCurrent = index === currentPhaseIndex
            const isLast = index === QUOTE_STATUS_PHASES.length - 1
            const isExpanded = expandedPhases.has(phase.key)

            return (
              <motion.div
                key={phase.key}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05, duration: 0.25 }}
              >
                <div
                  className={cn(
                    'relative w-full rounded-xl px-2 py-2 text-left transition-colors',
                    isCurrent &&
                      'bg-gradient-to-r from-primary/[0.06] via-primary/[0.03] to-transparent',
                    isExpanded && !isCurrent && 'bg-gray-50/60'
                  )}
                >
                  <Row className="items-stretch gap-3">
                    <Column className="relative w-8 shrink-0 items-center">
                      <div
                        className={cn(
                          'relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-all',
                          isComplete && 'bg-black text-white shadow-sm',
                          isCurrent &&
                            cn(
                              'bg-gradient-to-br text-white shadow-md ring-2 ring-offset-2',
                              ui.gradient,
                              ui.ring
                            ),
                          !isComplete &&
                            !isCurrent &&
                            'border border-gray-200/80 bg-white text-muted-foreground/70'
                        )}
                      >
                        {isComplete ? (
                          <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
                        ) : (
                          <Icon className="h-3.5 w-3.5" strokeWidth={2.25} />
                        )}
                      </div>
                      {!isLast && (
                        <div
                          className={cn(
                            'pointer-events-none absolute left-1/2 top-8 w-px -translate-x-1/2 -bottom-4',
                            index < currentPhaseIndex && 'bg-black/40',
                            index === currentPhaseIndex &&
                              'bg-gradient-to-b from-black/40 to-gray-200',
                            index > currentPhaseIndex && 'bg-gray-200'
                          )}
                        />
                      )}
                    </Column>

                    <Column className="min-w-0 flex-1 gap-0.5">
                      <button
                        type="button"
                        onClick={() => togglePhase(phase.key)}
                        aria-expanded={isExpanded}
                        className="w-full rounded-lg text-left transition-colors hover:bg-gray-50/80"
                      >
                        <Row className="h-8 items-center gap-2">
                          <span
                            className={cn(
                              'min-w-0 flex-1 text-sm leading-tight',
                              isCurrent
                                ? 'font-medium text-foreground'
                                : isComplete
                                  ? 'text-foreground/80'
                                  : 'text-muted-foreground'
                            )}
                          >
                            {t(phase.label)}
                          </span>
                          {isCurrent && !isExpanded && (
                            <Badge
                              variant={ui.badge}
                              size="sm"
                              className="px-1.5 py-0"
                            >
                              {currentLabel}
                            </Badge>
                          )}
                          <ChevronDown
                            className={cn(
                              'h-3.5 w-3.5 shrink-0 text-muted-foreground/60 transition-transform duration-200',
                              isExpanded && 'rotate-180'
                            )}
                          />
                        </Row>
                      </button>

                      <AnimatePresence initial={false}>
                        {isExpanded && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                          >
                            <Column className="gap-2 pt-1">
                              <span className="text-xs leading-relaxed text-muted-foreground">
                                {t(phase.description)}
                              </span>
                            </Column>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </Column>
                  </Row>
                </div>
              </motion.div>
            )
          })}
        </Column>
      </Column>
    </Column>
  )
}
